import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import {
    ActionIcon, Autocomplete, Avatar, Box, Button, Center, Container, Group,
    Loader, Paper, Stack, Text, Title, UnstyledButton,
} from '@mantine/core';
import { IconBrandGithub, IconHistory, IconLogout, IconShield, IconX } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { signInWithGitHub, signOut, type Me } from '../api';
import { useMe } from '../hooks/use-me';
import { useRepoHistory, type RepoHistoryEntry } from '../hooks/use-repo-history';

/**
 * Принимаем только локальные пути ('/...'), чтобы callbackUrl нельзя было
 * подсунуть как редирект на внешний домен. Также режем '//host' и '/\host'
 * — браузер их трактует как protocol-relative / схема-relative.
 */
function safeCallback(raw: string | null): string {
    if (!raw) return '/';
    if (!raw.startsWith('/')) return '/';
    if (raw.startsWith('//') || raw.startsWith('/\\')) return '/';
    return raw;
}

export function HomePage(): JSX.Element {
    const { data, isLoading } = useMe();
    const [params] = useSearchParams();
    const callbackUrl = safeCallback(params.get('callbackUrl'));

    return (
        <Container size={460} px="md" py="lg">
            {isLoading ? (
                <Center mih="60vh"><Loader /></Center>
            ) : data?.user ? (
                <SignedInHome user={data.user} isAdmin={Boolean(data.isAdmin)} callbackUrl={callbackUrl} />
            ) : (
                <SignedOutHome callbackUrl={callbackUrl} />
            )}
        </Container>
    );
}

function SignedOutHome({ callbackUrl }: { callbackUrl: string }): JSX.Element {
    return (
        <Stack gap="lg" mt="xl">
            <Stack gap={4}>
                <Title order={2}>ProductFlow</Title>
                <Text c="dimmed" size="sm">
                    Веб-просмотр pflow-проектов из GitHub. Зайдите через GitHub,
                    чтобы открыть репозиторий.
                </Text>
            </Stack>
            <Button
                onClick={() => { void signInWithGitHub(callbackUrl); }}
                leftSection={<IconBrandGithub size={18} />}
                size="md"
                fullWidth
            >
                Войти через GitHub
            </Button>
        </Stack>
    );
}

function SignedInHome({
    user, isAdmin, callbackUrl,
}: {
    user: NonNullable<Me['user']>;
    isAdmin: boolean;
    callbackUrl: string;
}): JSX.Element {
    const navigate    = useNavigate();
    const queryClient = useQueryClient();
    const [input, setInput] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const { history, addRepo, removeRepo } = useRepoHistory();

    // Если уже залогинен и в URL есть callbackUrl — отправляем туда сразу
    // (юзер пришёл с /r/... под анонимом, RepoPage редиректнул сюда, мы
    // уже авторизованы — нет смысла показывать форму открытия репо).
    useEffect(() => {
        if (callbackUrl && callbackUrl !== '/') {
            navigate(callbackUrl, { replace: true });
        }
    }, [callbackUrl, navigate]);

    const signOutMutation = useMutation({
        mutationFn: signOut,
        onSuccess:  () => {
            queryClient.setQueryData<Me>(['me'], { user: null });
            navigate('/');
        },
    });

    function openRepo(owner: string, repo: string): void {
        addRepo(owner, repo);
        navigate(`/r/${owner}/${repo}`);
    }

    function handleSubmit(e: FormEvent): void {
        e.preventDefault();
        setError(null);
        const parsed = parseRepoInput(input.trim());
        if (!parsed) {
            setError('Ожидаю `owner/repo` или GitHub URL.');
            return;
        }
        openRepo(parsed.owner, parsed.repo);
    }

    // Autocomplete-data: показываем историю как подсказки `owner/repo`.
    // Mantine сам фильтрует по подстроке при наборе.
    const autocompleteData = history.map(e => `${e.owner}/${e.repo}`);

    return (
        <Stack gap="lg" mt="md">
            <Group gap="sm" align="center" justify="space-between">
                <Group gap="sm">
                    {user.image && <Avatar src={user.image} alt={user.name ?? '?'} radius="xl" />}
                    <Stack gap={0}>
                        <Text fw={500}>{user.name ?? user.email ?? 'GitHub user'}</Text>
                        {user.email && <Text size="xs" c="dimmed">{user.email}</Text>}
                    </Stack>
                </Group>
                <Group gap="xs">
                    {isAdmin && (
                        <Button
                            component={Link}
                            to="/admin"
                            variant="light"
                            color="indigo"
                            size="compact-sm"
                            leftSection={<IconShield size={14} />}
                        >
                            Admin
                        </Button>
                    )}
                    <Button
                        variant="subtle"
                        color="gray"
                        size="compact-sm"
                        leftSection={<IconLogout size={14} />}
                        onClick={() => signOutMutation.mutate()}
                        loading={signOutMutation.isPending}
                    >
                        Выйти
                    </Button>
                </Group>
            </Group>

            <Stack gap={4}>
                <Title order={3}>Открыть репо</Title>
                <Text size="sm" c="dimmed">
                    Введите `owner/repo` или вставьте URL GitHub-репозитория с
                    pflow-проектом.
                </Text>
            </Stack>

            <form onSubmit={handleSubmit}>
                <Stack gap="sm">
                    <Autocomplete
                        placeholder="shtrihme/pflow"
                        value={input}
                        onChange={setInput}
                        data={autocompleteData}
                        error={error}
                        autoFocus
                        // При фокусе показываем всю историю даже если поле пустое.
                        limit={20}
                        comboboxProps={{ withinPortal: true }}
                    />
                    <Button type="submit" disabled={input.trim() === ''}>
                        Открыть
                    </Button>
                </Stack>
            </form>

            {history.length > 0 && (
                <RecentRepos
                    history={history}
                    onOpen={openRepo}
                    onRemove={removeRepo}
                />
            )}

            {import.meta.env.DEV && (
                <Box mt="sm">
                    <Text size="xs" c="dimmed" mb={4}>Dev-shortcut</Text>
                    <Button
                        variant="light"
                        color="gray"
                        size="xs"
                        onClick={() => navigate('/r/smoke/starter?fixture=starter')}
                    >
                        Открыть starter (фикстура)
                    </Button>
                </Box>
            )}
        </Stack>
    );
}

function RecentRepos({
    history, onOpen, onRemove,
}: {
    history:  RepoHistoryEntry[];
    onOpen:   (owner: string, repo: string) => void;
    onRemove: (owner: string, repo: string) => void;
}): JSX.Element {
    return (
        <Stack gap="xs">
            <Group gap={6} align="center">
                <IconHistory size={14} />
                <Text size="xs" c="dimmed" fw={500} tt="uppercase">Недавние</Text>
            </Group>
            <Stack gap={4}>
                {history.map(entry => (
                    <RecentRepoRow
                        key={`${entry.owner}/${entry.repo}`}
                        entry={entry}
                        onOpen={onOpen}
                        onRemove={onRemove}
                    />
                ))}
            </Stack>
        </Stack>
    );
}

function RecentRepoRow({
    entry, onOpen, onRemove,
}: {
    entry:    RepoHistoryEntry;
    onOpen:   (owner: string, repo: string) => void;
    onRemove: (owner: string, repo: string) => void;
}): JSX.Element {
    return (
        <Paper withBorder p={0} radius="sm">
            <Group gap={0} wrap="nowrap" justify="space-between" align="stretch">
                <UnstyledButton
                    onClick={() => onOpen(entry.owner, entry.repo)}
                    style={{ flex: 1, padding: '8px 10px' }}
                    aria-label={`Открыть ${entry.owner}/${entry.repo}`}
                >
                    <Group gap={6} wrap="nowrap">
                        <Text size="sm" c="dimmed">{entry.owner}/</Text>
                        <Text size="sm" fw={500}>{entry.repo}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">{formatAgo(entry.lastOpenedAt)}</Text>
                </UnstyledButton>
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="lg"
                    radius={0}
                    onClick={() => onRemove(entry.owner, entry.repo)}
                    aria-label={`Убрать ${entry.owner}/${entry.repo} из истории`}
                >
                    <IconX size={14} />
                </ActionIcon>
            </Group>
        </Paper>
    );
}

function formatAgo(ts: number): string {
    const diff = Date.now() - ts;
    if (diff < 60_000)         return 'только что';
    if (diff < 3_600_000)      return `${Math.floor(diff / 60_000)} мин назад`;
    if (diff < 86_400_000)     return `${Math.floor(diff / 3_600_000)} ч назад`;
    if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)} дн назад`;
    return new Date(ts).toLocaleDateString();
}

function parseRepoInput(input: string): { owner: string; repo: string } | null {
    if (input === '') return null;
    const urlMatch = input.match(/^https?:\/\/github\.com\/([^/]+)\/([^/?#]+)/);
    if (urlMatch) return { owner: urlMatch[1], repo: stripGit(urlMatch[2]) };
    const shortMatch = input.match(/^([A-Za-z0-9._-]+)\/([A-Za-z0-9._-]+)$/);
    if (shortMatch) return { owner: shortMatch[1], repo: stripGit(shortMatch[2]) };
    return null;
}

function stripGit(s: string): string {
    return s.endsWith('.git') ? s.slice(0, -4) : s;
}
